import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiRequest } from "./api";

type UseApiResult<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (value: T | null) => void;
};

export function useApi<T>(
  path: string | null,
  query?: Record<string, string | number | boolean | undefined | null>,
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(Boolean(path));
  const [nonce, setNonce] = useState(0);
  const queryKey = JSON.stringify(query ?? {});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!path) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiRequest<T>(path, { query })
      .then((result) => {
        if (cancelled || !mounted.current) return;
        setData(result);
      })
      .catch((cause: unknown) => {
        if (cancelled || !mounted.current) return;
        setError(
          cause instanceof ApiError ? cause.message : "Unable to load this data.",
        );
      })
      .finally(() => {
        if (cancelled || !mounted.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, queryKey, nonce]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return { data, error, loading, reload, setData };
}
